/**
 * VolantinoStimaModal — modal per scaricare il Volantino Stima A5 fronte/retro
 * con stile (layout × colore) e logo agenzia caricato dal profilo.
 *
 * Self-contained: fetch /api/mandates/flyer-manifest (logo, agent name, slug,
 * QR base64, URL stima short/full). Riusa l'engine `StaticTemplatePreview`
 * con template="volantino_stima" → quindi STESSO render della pagina
 * `VolantinoStimaAnteprima` ma in modal.
 *
 * Export:
 *   - PNG Fronte (1080w, A5 ratio) — perfetto per Instagram Story
 *   - PNG Retro  (1080w, A5 ratio)
 *   - PDF A5 fronte/retro — per stampa volantini
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { Download, FileText, X, Image as ImageIcon, ArrowSquareOut, PencilSimple, ArrowCounterClockwise, CaretDown } from "@phosphor-icons/react";
import StaticTemplatePreview, { LAYOUTS, COLORS } from "./StaticTemplatePreview";
import { CANONICAL_COLOR_ENTRIES } from "./staticTemplate/tokens";
import { VOLANTINO_STIMA_TEXTS } from "./staticTemplate/VolantinoStima";
import { exportA5MultiAsPdf, exportStaticAsPng, EXPORT_WIDTHS, smartDownloadBlob, isIOSDevice } from "@/lib/staticExport";
import { copyToClipboard } from "@/lib/clipboard";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

// iter v27 r8 — persistenza testi custom volantino in localStorage.
// Niente backend per ora: il volantino è personale e l'agente lo edita raramente.
// Salviamo l'oggetto { key: "testo" } sotto questa chiave dedicata.
const LOCAL_KEY = "pronto.volantino_stima.custom_texts.v1";
function loadCustomTexts() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch { return {}; }
}
function saveCustomTexts(obj) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(obj)); } catch { /* quota */ }
}

// Risolve URL relativo del logo (/api/public/agency/.../logo) → URL assoluto.
function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http://") || logoUrl.startsWith("https://") || logoUrl.startsWith("data:")) return logoUrl;
  if (logoUrl.startsWith("/api/")) return `${API}${logoUrl}`;
  return logoUrl;
}

export default function VolantinoStimaModal({ open, onClose }) {
  const [layout, setLayout] = useState("elegante");
  const [color, setColor] = useState("verde_brera");
  const [quality, setQuality] = useState("print"); // iter v27 r6 — print 300 DPI · web 150 DPI
  const [exporting, setExporting] = useState(null); // null | "png-fronte" | "png-retro" | "pdf"
  const [exportSeconds, setExportSeconds] = useState(0); // contatore live durante export
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  // iter v28 r361 — User M1270: «se uno ha già 'A' selezionato, fai chiudere
  // stile e colore, così uno va direttamente a salva/condividi». I pannelli
  // di selezione sono di default APERTI alla prima apertura, ma una volta
  // che l'utente ha confermato la combinazione (o l'ha lasciata sul default)
  // si chiudono in una pill compatta «Elegante · Verde Brera · cambia ↓».
  // Apertura forzata al primo render così l'utente vede le opzioni.
  const [pickerOpen, setPickerOpen] = useState(true);
  const previewRef = useRef(null);

  // iter v27 r6 — timer live durante l'export per non far sembrare "impallato"
  // il bottone "Genero PDF…". Mostra "Genero PDF… (Xs)" così l'utente sa
  // che sta lavorando e quanto tempo è passato.
  useEffect(() => {
    if (!exporting) { setExportSeconds(0); return; }
    setExportSeconds(0);
    const tick = setInterval(() => setExportSeconds((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [exporting]);

  // Carica i dati reali dell'agente all'apertura.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoading(true);
    // iter v28 r361 — Al riapri del modal, se la combinazione corrente è
    // il default (Elegante + Verde Brera + Print) significa che l'utente
    // l'ha lasciata e non vuole rivedere i picker → li chiudiamo.
    // Se invece ha CAMBIATO qualcosa dalla volta precedente, riapriamo.
    const isDefault = layout === "elegante" && color === "verde_brera" && quality === "print";
    setPickerOpen(!isDefault);
    api.get("/mandates/flyer-manifest")
      .then((r) => {
        if (cancel) return;
        setManifest(r.data);
        // Se l'utente ha già scelto un brand_color (HEX) nel profilo,
        // troviamo la palette più vicina (match esatto su accent o swatch).
        const brandHex = (r.data?.agency?.brand_color || "").toLowerCase();
        if (brandHex) {
          const match = Object.entries(COLORS).find(([, c]) =>
            c.accent.toLowerCase() === brandHex || c.swatch.toLowerCase() === brandHex
          );
          if (match) setColor(match[0]);
        }
      })
      .catch(() => {
        if (!cancel) toast.error("Impossibile caricare i dati del profilo.");
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [open]);

  const agency = useMemo(() => {
    if (!manifest?.agency) return null;
    return { ...manifest.agency, logo_url: resolveLogoUrl(manifest.agency.logo_url) };
  }, [manifest]);

  const property = { title: "Stima", city: agency?.area_short || "", photos: [] };
  const estimateUrl = agency?.estimate_url_full || "";
  const fileSlug = (manifest?.slug || "volantino").replace(/[^a-z0-9-]/gi, "-");

  // iter 313 r21 — preset qualità: stampa 600 DPI (max) o web 150 DPI (~1080px).
  const QUALITY_WIDTH = quality === "print" ? EXPORT_WIDTHS.volantino_stima : 1080;
  const QUALITY_JPEG = quality === "print" ? 0.95 : 0.85;

  const downloadPng = async (which) => {
    if (!previewRef.current) return;
    // iter v28 r187 — Caching anche per i PNG: se ho già il blob, scarica
    // istantaneo invece di rigenerare con html2canvas.
    if (pngReady[which]) {
      await smartDownloadBlob(pngReady[which].blob, pngReady[which].filename, "image/png");
      if (!isIOSDevice()) toast.success(`PNG ${which} scaricato.`);
      return;
    }
    setExporting(`png-${which}`);
    try {
      const pages = previewRef.current.querySelectorAll('[data-testid="brochure-page"]');
      const node = which === "fronte" ? pages[0] : pages[1];
      if (!node) throw new Error("Pagina non pronta");
      const filename = `volantino-${which}-${fileSlug}-${quality}.png`;
      // exportStaticAsPng RITORNA sempre il blob (e scarica side-effect).
      // Catturo il blob per cache successiva.
      const blob = await exportStaticAsPng({
        node,
        targetWidth: QUALITY_WIDTH,
        filename,
      });
      if (blob instanceof Blob) {
        setPngReady((prev) => ({ ...prev, [which]: { blob, filename } }));
      }
      toast.success(`PNG ${which} scaricato.`);
    } catch (e) {
      console.error(e);
      toast.error("Export fallito. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  // iter v27 r8: testi custom utente. null/empty = usa default.
  const [customTexts, setCustomTexts] = useState(() => loadCustomTexts());
  const [textsOpen, setTextsOpen] = useState(false);
  // iter v27 r7: dopo la generazione tengo blob+filename nello state.
  // Su iOS il "share" deve avvenire entro un gesture user FRESCO; se chiamato
  // dopo i 3s di generazione il browser ha già perso l'attivazione utente e
  // mostra una pagina bianca. Quindi separo "genera" da "condividi" e mostro
  // un secondo bottone che parte da un click sincrono.
  //
  // iter v28 r187 — User M1107: «ogni volta debba rigenerarlo». La persistenza
  // c'era SOLO su iOS. Ora valida anche su desktop/Android: dopo la prima
  // generazione il blob resta in memoria → click successivi "Scarica" usano
  // direttamente il blob (istantaneo, no html2canvas). Reset solo se l'utente
  // cambia layout/colore/qualità/testi (vedi useEffect sotto).
  const [pdfReady, setPdfReady] = useState(null); // {blob, filename}
  // iter v28 r187 — Stesso pattern per i PNG fronte/retro.
  const [pngReady, setPngReady] = useState({ fronte: null, retro: null }); // {blob, filename}
  // Pre-warming background: appena la preview è renderizzata e nessuno sta
  // facendo nulla, generiamo il PDF in silenzio così "Scarica" è istantaneo.
  const [prewarming, setPrewarming] = useState(false);

  // Reset i blob pre-generati quando l'utente cambia layout/colore/qualità/testi.
  useEffect(() => {
    setPdfReady(null);
    setPngReady({ fronte: null, retro: null });
  }, [layout, color, quality, customTexts]);

  // iter v28 r187 — Pre-warming background: dopo 1.5s che la preview è
  // renderizzata e nessuno sta esportando, generiamo il PDF in silenzio
  // così quando l'utente clicca "Scarica" il blob è già pronto → click
  // istantaneo. Se l'utente cambia config (vedi useEffect sopra), il blob
  // viene invalidato e ri-prewarmato.
  useEffect(() => {
    if (!open || loading || !manifest || !previewRef.current) return;
    if (pdfReady || exporting !== null) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled || !previewRef.current) return;
      const pages = Array.from(previewRef.current.querySelectorAll('[data-testid="brochure-page"]'));
      if (pages.length !== 2) return;
      setPrewarming(true);
      try {
        const filename = `volantino-${fileSlug}-${quality}.pdf`;
        const pdfBlob = await exportA5MultiAsPdf({
          pageNodes: pages,
          filename,
          autoSave: false,
          targetWidth: QUALITY_WIDTH,
          jpegQuality: QUALITY_JPEG,
        });
        if (!cancelled) setPdfReady({ blob: pdfBlob, filename });
      } catch (e) {
        // pre-warming è opportunistico: errori silenziosi.
        console.debug("Pre-warm PDF skipped:", e);
      } finally {
        if (!cancelled) setPrewarming(false);
      }
    }, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, loading, manifest, layout, color, quality, customTexts, pdfReady, exporting, fileSlug, QUALITY_WIDTH, QUALITY_JPEG]);

  // iter v27 r8 — handler edit testi: salva su localStorage e ri-renderizza.
  const setText = (key, value) => {
    const next = { ...customTexts };
    if (!value || value === VOLANTINO_STIMA_TEXTS[key]?.default) {
      delete next[key]; // niente custom = torno al default
    } else {
      next[key] = value;
    }
    setCustomTexts(next);
    saveCustomTexts(next);
  };
  const resetAllTexts = () => {
    setCustomTexts({});
    saveCustomTexts({});
    toast.success("Testi del volantino ripristinati ai valori di default.");
  };
  const editedCount = Object.keys(customTexts).length;

  const downloadPdf = async () => {
    if (!previewRef.current) return;
    // iter v27 r7 — se il PDF è già pronto, riusiamo il blob (no rigenerazione)
    if (pdfReady) {
      await smartDownloadBlob(pdfReady.blob, pdfReady.filename, "application/pdf");
      if (!isIOSDevice()) toast.success("Volantino PDF scaricato.");
      return;
    }
    setExporting("pdf");
    try {
      const pages = Array.from(previewRef.current.querySelectorAll('[data-testid="brochure-page"]'));
      if (pages.length !== 2) throw new Error("Volantino non pronto");
      const filename = `volantino-${fileSlug}-${quality}.pdf`;
      const pdfBlob = await exportA5MultiAsPdf({
        pageNodes: pages,
        filename,
        autoSave: false,
        targetWidth: QUALITY_WIDTH,
        jpegQuality: QUALITY_JPEG,
      });
      // iter v28 r187 — Persisto il blob anche su desktop. Click successivi
      // del bottone "Scarica di nuovo" useranno la cache istantaneamente.
      setPdfReady({ blob: pdfBlob, filename });
      if (isIOSDevice()) {
        // iter v27 r7 — su iOS NON faccio share automatico (gesture scaduto).
        // Mostro un bottone "Salva / Condividi" che parte da un nuovo gesture
        // user fresco → share-sheet nativo funziona pulito.
      } else {
        // Desktop / Android: download diretto al primo click.
        await smartDownloadBlob(pdfBlob, filename, "application/pdf");
        toast.success("Volantino PDF scaricato.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Export PDF fallito. Riprova.");
    } finally {
      setExporting(null);
    }
  };

  // iter v28 r187 — Condivisione veloce: usa Web Share API col blob già pronto.
  // Solo se il blob è in cache (pdfReady). Se non c'è, genera prima poi share.
  const sharePdf = async () => {
    if (!pdfReady) {
      toast.info("Genera prima il volantino, poi condividi.");
      return;
    }
    try {
      const file = new File([pdfReady.blob], pdfReady.filename, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Volantino stima" });
      } else {
        // Fallback: download
        await smartDownloadBlob(pdfReady.blob, pdfReady.filename, "application/pdf");
        toast.success("PDF scaricato · condividilo da Foto / File.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("Condivisione fallita.");
      }
    }
  };

  const copyLink = async () => {
    if (!estimateUrl) return;
    // iter 313 r17 — `navigator.clipboard.writeText` non funziona sempre su
    // iOS Safari (richiede user gesture sync; il toast spesso intercetta).
    // Helper robusto con fallback execCommand + Web Share.
    const result = await copyToClipboard(estimateUrl, { shareTitle: "Link stima" });
    if (result === "clipboard") {
      toast.success("Link copiato.");
    } else if (result === "share") {
      // Share sheet ha gestito il copy; non serve toast (l'utente vede l'OS dialog)
    } else {
      // Ultimo fallback: mostra l'URL così l'utente lo seleziona a mano
      toast.info("Tocca a lungo per copiare:", { description: estimateUrl, duration: 10000 });
    }
  };

  if (!open) return null;

  const hasLogo = !!agency?.logo_url;

  return (
    <div
      className="fixed inset-0 bg-black/55 z-50 flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      data-testid="volantino-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-5xl w-full p-5 sm:p-7 shadow-2xl max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
              Volantino "Stima casa" · A5 fronte/retro
            </span>
            <h3 className="font-editorial text-2xl text-[#1C1C1A] mt-1.5">
              Scegli stile e colore
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#6B6A65] hover:text-[#1C1C1A] text-2xl leading-none p-1"
            aria-label="Chiudi"
            data-testid="volantino-modal-close"
          >
            <X size={22} />
          </button>
        </div>
        <p className="text-sm text-[#6B6A65] mb-5 leading-snug">
          Brand bianco totale: solo il tuo logo, i tuoi contatti, il tuo QR.
          Stampabile A5 oppure scaricabile come PNG da pubblicare in story.
        </p>

        {/* Warning logo mancante */}
        {!loading && manifest && !hasLogo && (
          <div className="mb-5 flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="volantino-nologo-warn">
            <FileText size={15} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-amber-900 leading-snug">
              <span className="font-semibold">Logo agenzia non ancora caricato.</span>{" "}
              Verrà usato un lockup tipografico col nome dell'agenzia. Per il
              massimo impatto carica un logo (PNG trasparente 800×400) nel{" "}
              <a href="/dashboard/profile" className="underline font-semibold">tuo profilo</a>.
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-[280px_1fr] gap-5">
          {/* Pickers */}
          <div className="space-y-5">

            {/* iter v28 r361 — Header pill compatta sempre presente.
                Mostra rapidamente cosa è selezionato + bottone toggle. */}
            <div className="flex items-center justify-between gap-2 bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-lg px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#5A6A4D] font-bold">Selezione attuale</div>
                <div className="text-[12.5px] text-[#1C1C1A] font-semibold truncate" data-testid="volantino-current-pick">
                  {LAYOUTS[layout]?.name || layout} · {COLORS[color]?.name || (CANONICAL_COLOR_ENTRIES.find(([k]) => k === color)?.[1]?.name) || color}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                className="text-[11px] px-2.5 py-1 rounded-md font-semibold bg-white border border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E] flex items-center gap-1 shrink-0"
                data-testid="volantino-picker-toggle"
                aria-pressed={pickerOpen}
              >
                {pickerOpen ? "Chiudi" : "Cambia"}
                <CaretDown size={11} weight="bold" className={`transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            {pickerOpen && (<>
            {/* Layout */}
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] mb-2">Stile</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(LAYOUTS).map(([key, l]) => {
                  const active = layout === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setLayout(key)}
                      className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                        active ? "border-[#1C1C1A] bg-[#1C1C1A] text-white" : "border-[#E5E7E0] hover:border-[#7C8B6E] bg-white"
                      }`}
                      data-testid={`volantino-layout-${key}`}
                    >
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.16em]"
                        style={{ fontFamily: l.headingFont }}
                      >
                        {l.name}
                      </div>
                      <div className={`text-[10px] mt-1 leading-snug ${active ? "text-white/70" : "text-[#6B6A65]"}`}>
                        {l.description.split(".")[0]}.
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color */}
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] mb-2">Colore</div>
              <div className="grid grid-cols-5 sm:grid-cols-4 gap-2 sm:gap-2.5">
                {CANONICAL_COLOR_ENTRIES.map(([key, c]) => {
                  const active = color === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setColor(key)}
                      className="group flex flex-col items-center gap-1"
                      title={c.name}
                      data-testid={`volantino-color-${key}`}
                    >
                      <div
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-all ${
                          active ? "ring-2 ring-[#1C1C1A] ring-offset-2" : "ring-1 ring-[#E5E7E0] group-hover:ring-[#7C8B6E]"
                        }`}
                        style={{ background: c.swatch }}
                      />
                      <div className={`text-[8.5px] sm:text-[9px] text-center leading-tight ${active ? "text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"}`}>
                        {c.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* iter 313 r21 — Selettore qualità */}
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#7C8B6E] mb-2">Qualità</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setQuality("print")}
                  className={`text-left p-2.5 rounded-md border-2 transition-all ${
                    quality === "print"
                      ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                      : "border-[#E5E7E0] hover:border-[#7C8B6E] bg-white text-[#1C1C1A]"
                  }`}
                  data-testid="volantino-quality-print"
                >
                  <div className="text-[11px] font-bold">Stampa · 300 DPI</div>
                  <div className={`text-[10px] mt-0.5 leading-snug ${quality === "print" ? "text-white/70" : "text-[#6B6A65]"}`}>
                    Standard tipografico · ~1-2 MB
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setQuality("web")}
                  className={`text-left p-2.5 rounded-md border-2 transition-all ${
                    quality === "web"
                      ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                      : "border-[#E5E7E0] hover:border-[#7C8B6E] bg-white text-[#1C1C1A]"
                  }`}
                  data-testid="volantino-quality-web"
                >
                  <div className="text-[11px] font-bold">Web · 150 DPI</div>
                  <div className={`text-[10px] mt-0.5 leading-snug ${quality === "web" ? "text-white/70" : "text-[#6B6A65]"}`}>
                    Leggero · ottimo per WhatsApp/IG
                  </div>
                </button>
              </div>
            </div>
            </>)}{/* /pickerOpen */}

            {/* iter v27 r8 — Pannello "Modifica testi" collapsible.
                 Tutte le frasi del volantino sono editabili dall'utente,
                 rispettando il maxLength per non rompere il layout A5.
                 Persistenza in localStorage (LOCAL_KEY). */}
            <div className="border border-[#E5E7E0] rounded-lg" data-testid="volantino-texts-panel">
              <button
                type="button"
                onClick={() => setTextsOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-[#FAFAF7] transition-colors"
                data-testid="volantino-texts-toggle"
              >
                <div className="flex items-center gap-2">
                  <PencilSimple size={14} weight="bold" className="text-[#7C8B6E]" />
                  <div>
                    <div className="text-[12px] font-semibold text-[#1C1C1A]">Modifica testi</div>
                    <div className="text-[10.5px] text-[#6B6A65]">
                      {editedCount > 0
                        ? `${editedCount} frase${editedCount > 1 ? "i" : ""} personalizzat${editedCount > 1 ? "e" : "a"}.`
                        : "Riscrivi titolo, sottotitolo, citazione, promessa…"}
                    </div>
                  </div>
                </div>
                <CaretDown size={14} weight="bold" className={`text-[#9A968B] transition-transform ${textsOpen ? "rotate-180" : ""}`} />
              </button>
              {textsOpen && (
                <div className="border-t border-[#E5E7E0] p-3 space-y-4" data-testid="volantino-texts-body">
                  {/* Group: FRONTE */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">Fronte</div>
                      {editedCount > 0 && (
                        <button
                          type="button"
                          onClick={resetAllTexts}
                          className="flex items-center gap-1 text-[10px] text-[#6B6A65] hover:text-[#C66060]"
                          data-testid="volantino-texts-reset"
                          title="Riporta tutti i testi al default"
                        >
                          <ArrowCounterClockwise size={11} weight="bold" />
                          Ripristina tutto
                        </button>
                      )}
                    </div>
                    {[
                      "front_header_label", "front_title_line1", "front_title_line2",
                      "front_subtitle",
                      "front_bullet1_stat", "front_bullet1_text",
                      "front_bullet2_stat", "front_bullet2_text",
                      "front_bullet3_stat", "front_bullet3_text",
                      "front_qr_caption",
                    ].map((key) => (
                      <TextEditorRow
                        key={key}
                        textKey={key}
                        spec={VOLANTINO_STIMA_TEXTS[key]}
                        value={customTexts[key] ?? VOLANTINO_STIMA_TEXTS[key]?.default ?? ""}
                        onChange={(v) => setText(key, v)}
                      />
                    ))}
                  </div>
                  {/* Group: RETRO */}
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-2">Retro</div>
                    {[
                      "back_header_label",
                      "back_quote",
                      "back_agent_role",
                      "back_contacts_label",
                      "back_promise_label",
                      "back_promise_1", "back_promise_2", "back_promise_3",
                    ].map((key) => (
                      <TextEditorRow
                        key={key}
                        textKey={key}
                        spec={VOLANTINO_STIMA_TEXTS[key]}
                        value={customTexts[key] ?? VOLANTINO_STIMA_TEXTS[key]?.default ?? ""}
                        onChange={(v) => setText(key, v)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Download */}
            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={downloadPdf}
                disabled={exporting !== null || loading || !manifest}
                className={`w-full py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${
                  pdfReady
                    ? "bg-[#7C8B6E] text-white hover:bg-[#6A7860]"
                    : "bg-[#1C1C1A] text-white hover:bg-black"
                }`}
                data-testid="volantino-pdf-btn"
              >
                <Download size={16} weight="bold" />
                {exporting === "pdf"
                  ? `Genero PDF… ${exportSeconds > 0 ? `(${exportSeconds}s)` : ""}`
                  : pdfReady
                  ? (isIOSDevice() ? "Salva su File · Condividi" : "Scarica di nuovo · veloce")
                  : prewarming
                  ? "Preparo PDF in background…"
                  : "Scarica PDF A5 (stampa)"}
              </button>
              {/* iter v28 r187 — Bottone "Condividi" istantaneo (Web Share API)
                  visibile solo dopo prima generazione. Permette di mandare il
                  PDF via WhatsApp/Mail senza rigenerarlo. */}
              {pdfReady && exporting !== "pdf" && typeof navigator !== "undefined" && navigator.share && (
                <button
                  type="button"
                  onClick={sharePdf}
                  className="w-full py-2.5 bg-white border border-[#7C8B6E] text-[#1C1C1A] rounded-lg text-[12.5px] font-semibold hover:bg-[#F1F4ED] flex items-center justify-center gap-1.5"
                  data-testid="volantino-share-btn"
                >
                  <ArrowSquareOut size={13} weight="bold" />
                  Condividi · WhatsApp / Mail
                </button>
              )}
              {exporting === "pdf" && (
                <p className="text-[10.5px] text-[#9A968B] text-center -mt-1 leading-snug">
                  Sto preparando il volantino. Resta in questa schermata.
                </p>
              )}
              {pdfReady && exporting !== "pdf" && (
                <p className="text-[10.5px] text-[#7C8B6E] text-center -mt-1 leading-snug font-medium">
                  Volantino pronto · ri-scarica e condividi in 1 click, senza attesa.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => downloadPng("fronte")}
                  disabled={exporting !== null || loading || !manifest}
                  className={`py-2.5 rounded-lg text-[12.5px] font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5 ${
                    pngReady.fronte ? "bg-[#7C8B6E] text-white hover:bg-[#6A7860]" : "bg-[#1C1C1A] text-white hover:bg-black"
                  }`}
                  data-testid="volantino-png-fronte-btn"
                >
                  <ImageIcon size={14} weight="bold" />
                  {exporting === "png-fronte" ? "Genero…" : pngReady.fronte ? "PNG fronte · veloce" : "PNG fronte"}
                </button>
                <button
                  type="button"
                  onClick={() => downloadPng("retro")}
                  disabled={exporting !== null || loading || !manifest}
                  className={`py-2.5 rounded-lg text-[12.5px] font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5 ${
                    pngReady.retro ? "bg-[#7C8B6E] text-white hover:bg-[#6A7860]" : "bg-[#1C1C1A] text-white hover:bg-black"
                  }`}
                  data-testid="volantino-png-retro-btn"
                >
                  <ImageIcon size={14} weight="bold" />
                  {exporting === "png-retro" ? "Genero…" : pngReady.retro ? "PNG retro · veloce" : "PNG retro"}
                </button>
              </div>
              {estimateUrl && (
                <button
                  type="button"
                  onClick={copyLink}
                  className="w-full py-2 bg-white text-[#1C1C1A] border border-[#E5E7E0] rounded-lg text-[12px] font-semibold hover:bg-[#FAFAF7] flex items-center justify-center gap-1.5"
                  data-testid="volantino-copy-link"
                >
                  <ArrowSquareOut size={13} weight="bold" />
                  Copia link stima
                </button>
              )}
            </div>
          </div>

          {/* Live preview — fronte + retro */}
          <div
            className="bg-gradient-to-br from-[#F1F4ED] to-[#E5E7E0] rounded-xl p-3 sm:p-4"
            data-testid="volantino-preview-area"
            ref={previewRef}
          >
            {loading || !agency ? (
              <div className="h-[500px] flex items-center justify-center text-[#6B6A65] italic text-sm">
                Caricamento dati profilo…
              </div>
            ) : (
              <StaticTemplatePreview
                template="volantino_stima"
                layout={layout}
                color={color}
                property={property}
                agency={agency}
                customTexts={customTexts}
              />
            )}
          </div>
        </div>

        {/* iter v28 r361 — User M1270: «trucco da agente, rendilo più visibile».
            Promosso a card sage del logo Pronto con icona grande, titolo serif
            e bordo brand. Era nascosto sotto una fascia beige, ora salta
            all'occhio appena scrolli verso il basso.
            iter v28 r361 fix — User M1271: bg = #7C8B6E (verde logo Pronto). */}
        <div
          className="mt-5 pt-5 border-t border-[#E5E7E0]"
          data-testid="volantino-trucco-ig"
        >
          <div className="bg-[#7C8B6E] text-white border border-[#7C8B6E] rounded-2xl p-5 sm:p-6 flex items-start gap-4 shadow-[0_8px_24px_-12px_rgba(124,139,110,0.45)]">
            <div className="w-12 h-12 rounded-full bg-white text-[#7C8B6E] flex items-center justify-center shrink-0 mt-0.5">
              <ArrowSquareOut size={22} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-white/80 mb-1.5">
                Trucco da agente
              </div>
              <div className="font-editorial text-[18px] sm:text-[20px] text-white leading-tight mb-2">
                Fai funzionare il QR anche dentro le Stories di Instagram
              </div>
              <ol className="text-[13.5px] text-white/90 leading-relaxed space-y-1 list-decimal list-inside marker:text-[#F1F4ED] marker:font-bold">
                <li>Scarica il <strong className="text-white">PNG fronte</strong></li>
                <li>Caricalo in una nuova story</li>
                <li>Aggiungi lo sticker "<strong className="text-white">Link</strong>" col link della tua landing (sotto al pulsante "Copia link stima")</li>
                <li>Posiziona lo sticker <em>sopra al QR</em></li>
              </ol>
              <div className="text-[12.5px] text-white/85 mt-3 italic">
                Risultato: chi tocca il QR (anche se è solo una foto in story) apre direttamente la tua landing.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// iter v27 r8 — Riga editor singola.
// Mostra label + counter caratteri + input/textarea con maxLength enforced.
function TextEditorRow({ textKey, spec, value, onChange }) {
  if (!spec) return null;
  const remaining = spec.maxLength - (value || "").length;
  const isMultiline = !!spec.multiline;
  const isOver = remaining < 0;
  const isWarn = remaining >= 0 && remaining <= 10;
  return (
    <div className="mb-2.5" data-testid={`volantino-text-row-${textKey}`}>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[10.5px] text-[#6B6A65] font-medium">{spec.label}</label>
        <span className={`text-[9.5px] tabular-nums ${isOver ? "text-[#C66060] font-bold" : isWarn ? "text-[#D98E1E]" : "text-[#9A968B]"}`}>
          {Math.max(0, value?.length || 0)}/{spec.maxLength}
        </span>
      </div>
      {isMultiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, spec.maxLength))}
          maxLength={spec.maxLength}
          rows={2}
          className="w-full px-2.5 py-1.5 border border-[#E5E7E0] rounded text-[12px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E] resize-none leading-snug"
          data-testid={`volantino-text-input-${textKey}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, spec.maxLength))}
          maxLength={spec.maxLength}
          className="w-full px-2.5 py-1.5 border border-[#E5E7E0] rounded text-[12px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
          data-testid={`volantino-text-input-${textKey}`}
        />
      )}
    </div>
  );
}

